import { EMPTY_TERMS_DOC, TERMS_HEADING_LEVELS, } from '@zuruflex/types';
import { z } from 'zod';
/**
 * The one gate between a partner's editor and the database.
 *
 * A partner's terms are the only free-form content on Zuruflex that another
 * customer is shown, and they arrive as JSON from a browser — which means
 * they arrive from whatever anyone cares to POST. The defence is an
 * allow-list rather than a scrubber: every node type, every mark and every
 * attribute a document may contain is named below, and a document that
 * mentions anything else is **refused** rather than cleaned up and stored.
 *
 * Refusing rather than stripping is deliberate. A sanitiser silently
 * changes what a partner wrote, which for a legal document is the worst
 * outcome available — they would sign off a clause and we would publish a
 * different one. Our own editor cannot produce anything outside this
 * schema, so a document that fails is either a bug or an attack, and both
 * want an error rather than a quiet edit.
 */
/** Total characters of text. Roughly forty pages — far past any real terms. */
export const MAX_TERMS_CHARACTERS = 60_000;
/**
 * How deep the tree may nest.
 *
 * `doc > list > listItem > paragraph > text` is four, which is the deepest
 * anything the editor can produce goes. The cap is here because depth, not
 * length, is what turns a recursive renderer into a stack overflow, and
 * the phone is the one that would fall over.
 */
export const MAX_TERMS_DEPTH = 6;
/**
 * Where a link may point.
 *
 * Absolute http(s) only. `javascript:` is the obvious one to keep out, but
 * `mailto:` and relative paths are excluded too: this document is rendered
 * inside three apps and a website, and a relative href resolves against
 * whichever of them is showing it.
 */
const LINK_HREF = /^https?:\/\/[^\s<>"]+$/i;
const markSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('bold') }).strict(),
    z.object({ type: z.literal('italic') }).strict(),
    z
        .object({
        type: z.literal('link'),
        attrs: z
            .object({
            href: z.string().max(2048).regex(LINK_HREF, 'Links must be absolute http(s) URLs'),
        })
            // TipTap's link extension writes target and rel alongside href. They
            // are dropped rather than refused: they are the renderer's decision
            // on each platform, not the author's.
            .passthrough()
            .transform((attrs) => ({ href: attrs.href })),
    })
        .strict(),
]);
const textSchema = z
    .object({
    type: z.literal('text'),
    text: z.string().min(1),
    marks: z.array(markSchema).max(8).optional(),
})
    .strict();
const hardBreakSchema = z.object({ type: z.literal('hardBreak') }).strict();
/** An inline run: text with formatting, or a line break inside a paragraph. */
const inlineSchema = z.union([textSchema, hardBreakSchema]);
const paragraphSchema = z
    .object({
    type: z.literal('paragraph'),
    content: z.array(inlineSchema).optional(),
})
    .strict();
const headingSchema = z
    .object({
    type: z.literal('heading'),
    attrs: z.object({ level: z.union([z.literal(2), z.literal(3)]) }).strict(),
    content: z.array(inlineSchema).optional(),
})
    .strict();
const listItemSchema = z
    .object({
    type: z.literal('listItem'),
    // A list item holds paragraphs and nothing else: no nested lists, which
    // is a depth the renderers would each have to indent differently.
    content: z.array(paragraphSchema).min(1),
})
    .strict();
const bulletListSchema = z
    .object({
    type: z.literal('bulletList'),
    content: z.array(listItemSchema).min(1),
})
    .strict();
const orderedListSchema = z
    .object({
    type: z.literal('orderedList'),
    content: z.array(listItemSchema).min(1),
    // TipTap tracks where an ordered list starts. Nothing reads it, so it
    // is dropped rather than stored.
})
    .strict();
const blockSchema = z.union([paragraphSchema, headingSchema, bulletListSchema, orderedListSchema]);
/**
 * The document, as everything that writes one must validate it.
 *
 * Note that this is a *parse*, not a check: what comes out is a new object
 * built only from the fields named above, so an attribute nobody thought
 * to forbid cannot survive by being ignored.
 */
export const termsDocSchema = z
    .object({
    type: z.literal('doc'),
    content: z.array(blockSchema).max(500).optional(),
})
    .strict()
    .superRefine((doc, ctx) => {
    const text = termsPlainText(doc);
    if (text.length > MAX_TERMS_CHARACTERS) {
        ctx.addIssue({
            code: z.ZodIssueCode.too_big,
            maximum: MAX_TERMS_CHARACTERS,
            type: 'string',
            inclusive: true,
            message: `Terms may be at most ${MAX_TERMS_CHARACTERS} characters`,
        });
    }
    if (termsDepth(doc) > MAX_TERMS_DEPTH) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Terms are nested too deeply' });
    }
});
function termsDepth(node, depth = 1) {
    const children = node.content;
    if (!children?.length)
        return depth;
    return Math.max(...children.map((child) => termsDepth(child, depth + 1)));
}
/**
 * The document as running text.
 *
 * Denormalised onto the row on every write, because three readers want the
 * prose without the tree: WhatsApp, which cannot render a heading; search;
 * and the plain-text half of an email. Block boundaries become newlines
 * and list items keep a bullet, so what comes out reads like the document
 * rather than like its words concatenated.
 */
export function termsPlainText(doc) {
    const blocks = [];
    const inline = (node) => node.type === 'hardBreak'
        ? '\n'
        : node.type === 'text'
            ? (node.text ?? '')
            : (node.content ?? []).map(inline).join('');
    const walk = (node, bullet = '') => {
        switch (node.type) {
            case 'paragraph':
            case 'heading':
                blocks.push(bullet + (node.content ?? []).map(inline).join('').trim());
                break;
            case 'bulletList':
                (node.content ?? []).forEach((item) => walk(item, '• '));
                break;
            case 'orderedList':
                (node.content ?? []).forEach((item, index) => walk(item, `${index + 1}. `));
                break;
            case 'listItem':
                (node.content ?? []).forEach((child, index) => walk(child, index === 0 ? bullet : '  '));
                break;
            default:
                break;
        }
    };
    (doc.content ?? []).forEach((node) => walk(node));
    return blocks.filter((block) => block.length > 0).join('\n');
}
/**
 * Whether there is anything here to publish.
 *
 * An untouched TipTap editor is not an empty document — it holds one empty
 * paragraph — so "is the content array empty" is the wrong question and
 * would let a partner submit a blank page for review.
 */
export function isTermsDocEmpty(doc) {
    return termsPlainText(doc).trim().length === 0;
}
/** What a partner who has never opened the editor starts from. */
export function emptyTermsDoc() {
    return structuredClone(EMPTY_TERMS_DOC);
}
/** The heading levels the editor offers, so a toolbar cannot offer a fifth. */
export const TERMS_EDITOR_HEADING_LEVELS = TERMS_HEADING_LEVELS;
